import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Avatar,
  AvatarGroup,
  Typography,
  Tooltip,
  CircularProgress,
  Chip,
  Paper,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
  Button,
  alpha,
  useTheme,
  Fade,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CommentIcon from "@mui/icons-material/Comment";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PersonIcon from "@mui/icons-material/Person";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useNavigate } from "react-router-dom";

import type { Task } from "../../utils/task";
import { handleDate } from "../../utils/date-handlers";
import {
  getTaskStatusContainerBgColor,
  getTaskStatusContainerTextColor,
  getTaskStatusLabel,
} from "../../utils/task-status.utils";
import {
  getImportanceContainerColor,
  getImportanceContainerText,
} from "../../utils/task-importance.utils";
import { TaskStatus } from "../../enums/task-status.enum";
import { TaskImportance } from "../../enums/task-importance.enum";

/* -------------------------------------------------------------------------- */
/*                             Helper Functions                               */
/* -------------------------------------------------------------------------- */

/**
 * Safe alpha helper - converts color names to hex before applying alpha
 */
const colorNameToHex: Record<string, string> = {
  orange: "#ff9800",
  red: "#f44336",
  yellow: "#ffeb3b",
  green: "#4caf50",
  blue: "#2196f3",
  purple: "#9c27b0",
  pink: "#e91e63",
  cyan: "#00bcd4",
  teal: "#009688",
  lime: "#cddc39",
  amber: "#ffc107",
  gray: "#9e9e9e",
  grey: "#9e9e9e",
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const safeAlpha = (_theme: any, color: string, opacity: number): string => {
  const lowerColor = color.toLowerCase();
  const hexColor = colorNameToHex[lowerColor] || color;
  return alpha(hexColor, opacity);
};

/* -------------------------------------------------------------------------- */
/*                                  Types                                     */
/* -------------------------------------------------------------------------- */

interface TasksTableProps {
  rows: Task[];
  isLoading: boolean;
  setOpen: (value: boolean) => void;
  setTaskId: (id: number) => void;
  currentUserId: number;
}

type ColumnId =
  | "createdBy"
  | "title"
  | "project"
  | "type"
  | "description"
  | "startDate"
  | "endDate"
  | "status"
  | "assignedTo"
  | "importance"
  | "counts"
  | "actions";

type ColumnDef = {
  id: ColumnId;
  label: string;
  minWidth?: number;
  maxWidth?: number;
  align?: "left" | "right" | "center";
  /** render cell */
  render: (row: Task, helpers: RenderHelpers) => React.ReactNode;
  /** true = cannot be hidden */
  locked?: boolean;
};

type RenderHelpers = {
  navigate: ReturnType<typeof useNavigate>;
  currentUserId: number;
  openStatus: (taskId: number) => void;
};

/* -------------------------------------------------------------------------- */
/*                         Status Icon Component                              */
/* -------------------------------------------------------------------------- */

const StatusIcon: React.FC<{ status: TaskStatus }> = ({ status }) => {
  const iconSx = { fontSize: 16, mr: 0.5 };
  
  switch (status) {
    case TaskStatus.DONE:
      return <CheckCircleIcon sx={iconSx} />;
    case TaskStatus.IN_PROGRESS:
      return <PlayCircleFilledIcon sx={iconSx} />;
    case TaskStatus.POSTPONED:
      return <PauseCircleIcon sx={iconSx} />;
    case TaskStatus.REVIEW:
      return <VisibilityIcon sx={iconSx} />;
    case TaskStatus.TODO:
    default:
      return <ScheduleIcon sx={iconSx} />;
  }
};

/* -------------------------------------------------------------------------- */
/*                       Actions Cell Component                               */
/* -------------------------------------------------------------------------- */

const ActionsCell: React.FC<{ row: Task; navigate: ReturnType<typeof useNavigate> }> = ({ row, navigate }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();

  return (
    <>
      <Tooltip title="Actions" arrow>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
          sx={{
            width: 32,
            height: 32,
            background: theme.palette.mode === "dark"
              ? alpha(theme.palette.primary.main, 0.12)
              : alpha(theme.palette.primary.main, 0.08),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            transition: "all 0.2s ease",
            "&:hover": {
              background: alpha(theme.palette.primary.main, 0.18),
              transform: "scale(1.1) rotate(90deg)",
            },
          }}
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.palette.mode === "dark"
              ? `0 8px 32px ${alpha("#000", 0.5)}`
              : `0 8px 32px ${alpha("#000", 0.15)}`,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/task/${row.id}`);
            setAnchorEl(null);
          }}
          sx={{
            gap: 1.5,
            py: 1,
            "&:hover": {
              background: alpha(theme.palette.info.main, 0.1),
            },
          }}
        >
          <VisibilityIcon fontSize="small" sx={{ color: "info.main" }} />
          <Typography variant="body2" fontWeight={600}>
            View Details
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/edit-task/${row.id}`);
            setAnchorEl(null);
          }}
          sx={{
            gap: 1.5,
            py: 1,
            "&:hover": {
              background: alpha(theme.palette.warning.main, 0.1),
            },
          }}
        >
          <EditIcon fontSize="small" sx={{ color: "warning.main" }} />
          <Typography variant="body2" fontWeight={600}>
            Edit Task
          </Typography>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            // Add delete logic here
            setAnchorEl(null);
          }}
          sx={{
            gap: 1.5,
            py: 1,
            "&:hover": {
              background: alpha(theme.palette.error.main, 0.1),
            },
          }}
        >
          <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
          <Typography variant="body2" fontWeight={600}>
            Delete Task
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

/* -------------------------------------------------------------------------- */
/*                               Column config                                */
/* -------------------------------------------------------------------------- */

const getColumns = (): ColumnDef[] => [
  {
    id: "createdBy",
    label: "Created By",
    minWidth: 180,
    render: (row) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const createdBy = (row as any)?.createdBy;
      const img =
        createdBy?.imageUrl ||
        createdBy?.profile?.imageUrl ||
        undefined;
      const name = createdBy?.username || "Unknown";
      const initial = name ? name.charAt(0).toUpperCase() : "?";
      
      return (
        <Box display="flex" alignItems="center" minWidth={160}>
          <Box
            sx={{
              p: 0.4,
              borderRadius: "50%",
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? `conic-gradient(from 180deg at 50% 50%, ${alpha(theme.palette.primary.main, 0.4)}, ${alpha(theme.palette.secondary.main, 0.4)}, ${alpha(theme.palette.primary.main, 0.4)})`
                  : `conic-gradient(from 180deg at 50% 50%, ${alpha(theme.palette.primary.main, 0.3)}, ${alpha(theme.palette.secondary.main, 0.3)}, ${alpha(theme.palette.primary.main, 0.3)})`,
              mr: 1.5,
              boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
            }}
          >
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                fontSize: 14, 
                fontWeight: 700,
                border: (theme) => `2px solid ${theme.palette.background.paper}`,
              }} 
              src={img}
            >
              {initial}
            </Avatar>
          </Box>
          <Tooltip title={name || "Unknown user"} arrow>
            <Typography
              variant="body2"
              sx={{
                maxWidth: 120,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: 600,
                letterSpacing: 0.2,
              }}
            >
              {name}
            </Typography>
          </Tooltip>
        </Box>
      );
    },
  },
  {
    id: "title",
    label: "Title",
    minWidth: 200,
    maxWidth: 350,
    locked: true, // keep always visible
    render: (row, { navigate }) => (
      <Tooltip title={row.title} arrow>
        <Typography
          onClick={() => navigate(`/task/${row.id}`)}
          sx={{
            display: "block",
            width: "100%",
            color: (theme) => theme.palette.primary.main,
            fontWeight: 700,
            fontSize: "0.9rem",
            letterSpacing: 0.2,
            transition: "all 0.2s ease",
            "&:hover": { 
              textDecoration: "underline",
              color: (theme) => theme.palette.primary.light,
            },
            cursor: "pointer",
          }}
        >
          {row.title}
        </Typography>
      </Tooltip>
    ),
  },
  {
    id: "project",
    label: "Project",
    minWidth: 140,
    maxWidth: 240,
    render: (row) => (
      <Tooltip title={row.projectName || "No project"} arrow>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            background: (theme) =>
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            borderRadius: 1.5,
            px: 1.25,
            py: 0.65,
            fontSize: "0.8rem",
            fontWeight: 600,
            maxWidth: 240,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "primary.main",
              opacity: 0.9,
              flex: "0 0 auto",
              boxShadow: (theme) => `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}`,
            }}
          />
          <span>{row.projectName || "—"}</span>
        </Box>
      </Tooltip>
    ),
  },
  {
    id: "type",
    label: "Type",
    minWidth: 140,
    render: (row) => (
      <Tooltip title={row.type || "No type specified"} arrow>
        <Chip
          label={row.type || "—"}
          size="small"
          sx={{
            background: (theme) =>
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
            border: (theme) => `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
            fontSize: "0.75rem",
            fontWeight: 600,
            letterSpacing: 0.3,
            height: 26,
            maxWidth: 140,
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "scale(1.05)",
              boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.secondary.main, 0.2)}`,
            },
          }}
        />
      </Tooltip>
    ),
  },
  {
    id: "description",
    label: "Description",
    minWidth: 150,
    maxWidth: 300,
    render: (row) => (
      <Tooltip title={row.description || "No description"} arrow>
        <Typography
          sx={{
            display: "block",
            width: "100%",
            fontSize: "0.85rem",
            color: (t) => t.palette.text.secondary,
            fontStyle: row.description ? "normal" : "italic",
            opacity: row.description ? 1 : 0.6,
          }}
        >
          {row.description || "No description"}
        </Typography>
      </Tooltip>
    ),
  },
  {
    id: "startDate",
    label: "Start Date",
    minWidth: 120,
    render: (row) => (
      <Chip
        label={row.startDate ? handleDate(row.startDate) : "—"}
        size="small"
        variant="outlined"
        sx={{
          fontSize: "0.75rem",
          fontWeight: 600,
          borderColor: (theme) => alpha(theme.palette.divider, 0.3),
          color: (theme) => theme.palette.text.secondary,
        }}
      />
    ),
  },
  {
    id: "endDate",
    label: "End Date",
    minWidth: 120,
    render: (row) => {
      const now = new Date().getTime();
      const isOverdue =
        !!row.endDate &&
        new Date(row.endDate).getTime() < now &&
        row.status !== TaskStatus.DONE;

      return (
        <Chip
          label={row.endDate ? handleDate(row.endDate) : "—"}
          size="small"
          sx={{
            fontSize: "0.75rem",
            fontWeight: 700,
            ...(isOverdue
              ? {
                  background: (t) =>
                    `linear-gradient(135deg, ${alpha(t.palette.error.main, 0.15)} 0%, ${alpha(t.palette.error.main, 0.08)} 100%)`,
                  color: (t) => t.palette.error.main,
                  border: (t) => `1px solid ${alpha(t.palette.error.main, 0.4)}`,
                  boxShadow: (t) => `0 2px 8px ${alpha(t.palette.error.main, 0.2)}`,
                  animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.7 },
                  },
                }
              : {
                  borderColor: (theme) => alpha(theme.palette.divider, 0.3),
                  color: (theme) => theme.palette.text.secondary,
                }),
          }}
        />
      );
    },
  },
  {
    id: "status",
    label: "My Status",
    minWidth: 150,
    locked: true, // keep always visible (it triggers dialog)
    align: "center",
    render: (row, { openStatus, currentUserId }) => {
      // Get userTask status for current user
      const rowData = row as any;
      const assignedUsers = rowData.assignedUsers || [];
      const userTask = assignedUsers.find((au: any) => au.userId === currentUserId || au.user?.id === currentUserId);
      const userTaskStatus = userTask?.status || row.status; // Fallback to task status if not found
      
      return (
        <Chip
          icon={<StatusIcon status={userTaskStatus as TaskStatus} />}
          label={getTaskStatusLabel(userTaskStatus as TaskStatus)}
          onClick={() => openStatus(row.id)}
          sx={{
            borderRadius: 2,
            px: 0.5,
            py: 0.5,
            height: 32,
            background: getTaskStatusContainerBgColor(userTaskStatus as TaskStatus),
            color: getTaskStatusContainerTextColor(userTaskStatus as TaskStatus),
            fontSize: "0.75rem",
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            cursor: "pointer",
            minWidth: 110,
            border: `1.5px solid ${alpha(getTaskStatusContainerTextColor(userTaskStatus as TaskStatus), 0.2)}`,
            boxShadow: `0 2px 8px ${alpha(getTaskStatusContainerBgColor(userTaskStatus as TaskStatus), 0.3)}`,
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-2px) scale(1.05)",
              boxShadow: `0 4px 16px ${alpha(getTaskStatusContainerBgColor(userTaskStatus as TaskStatus), 0.4)}`,
            },
            "& .MuiChip-icon": {
              color: "inherit",
              marginLeft: "8px",
            },
          }}
        />
      );
    },
  },
  {
    id: "assignedTo",
    label: "Assigned To",
    minWidth: 160,
    render: (row) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rowData = row as any;
      // Handle both direct user array and nested user structure
      const users = rowData.assignedUsers || [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mappedUsers = users.map((item: any) => {
        // If it's a nested structure with userId and user object
        if (item.user) {
          return {
            id: item.user.id,
            username: item.user.username,
            imageUrl: item.user.imageUrl || item.user?.profile?.imageUrl,
          };
        }
        // If it's a direct user object
        return {
          id: item.id,
          username: item.username,
          imageUrl: item.imageUrl || item?.profile?.imageUrl,
        };
      });

      console.log(`[AssignedTo] Row ${row.id}: users=`, mappedUsers);

      if (mappedUsers.length === 0) {
        return (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
            Unassigned
          </Typography>
        );
      }

      return (
        <AvatarGroup
          max={5}
          sx={{
            "& .MuiAvatar-root": {
              width: 28,
              height: 28,
              fontSize: 12,
              fontWeight: 700,
              border: (_t) => `2px solid ${_t.palette.background.paper}`,
              boxShadow: `0 2px 8px ${alpha("#000", 0.15)}`,
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "scale(1.15) translateY(-2px)",
                zIndex: 10,
              },
            },
          }}
        >
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {mappedUsers.map((user: any) => (
            <Tooltip key={user.id} title={user.username} arrow>
              <Avatar src={user.imageUrl || undefined}>
                {user.username?.charAt(0)?.toUpperCase()}
              </Avatar>
            </Tooltip>
          ))}
        </AvatarGroup>
      );
    },
  },
  {
    id: "importance",
    label: "Importance",
    minWidth: 140,
    align: "center",
    render: (row) => (
      <Chip
        label={getImportanceContainerText(row.importance as TaskImportance)}
        sx={{
          background: (theme) => {
            const impColor = getImportanceContainerColor(row.importance as TaskImportance);
            return `linear-gradient(135deg, ${impColor} 0%, ${safeAlpha(theme, impColor, 0.8)} 100%)`;
          },
          color: (theme) => theme.palette.common.white,
          fontSize: "0.75rem",
          height: 28,
          borderRadius: 1.5,
          fontWeight: 800,
          letterSpacing: 0.3,
          textTransform: "uppercase",
          boxShadow: (theme) => {
            const impColor = getImportanceContainerColor(row.importance as TaskImportance);
            return `0 2px 8px ${safeAlpha(theme, impColor, 0.3)}`;
          },
          border: `1.5px solid ${alpha("#fff", 0.2)}`,
          transition: "all 0.2s ease",
          "&:hover": {
            transform: "scale(1.08)",
            boxShadow: (theme) => {
              const impColor = getImportanceContainerColor(row.importance as TaskImportance);
              return `0 4px 16px ${safeAlpha(theme, impColor, 0.4)}`;
            },
          },
        }}
      />
    ),
  },
  {
    id: "counts",
    label: "Activity",
    minWidth: 200,
    render: (row) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rowData = row as any;
      return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Tooltip key={`comments-${row.id}`} title="Comments" arrow>
          <Chip
            icon={<CommentIcon sx={{ fontSize: 14 }} />}
            label={rowData.commentCount ?? 0}
            size="small"
            sx={{
              height: 24,
              fontSize: "0.7rem",
              fontWeight: 700,
              background: (_t) =>
                _t.palette.mode === "dark"
                  ? alpha(_t.palette.info.main, 0.12)
                  : alpha(_t.palette.info.main, 0.08),
              color: (_t) => _t.palette.info.main,
              border: (_t) => `1px solid ${alpha(_t.palette.info.main, 0.2)}`,
              "& .MuiChip-icon": {
                color: "inherit",
              },
            }}
          />
        </Tooltip>

        <Tooltip key={`attachments-${row.id}`} title="Attachments" arrow>
          <Chip
            icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
            label={rowData.attachmentCount ?? 0}
            size="small"
            sx={{
              height: 24,
              fontSize: "0.7rem",
              fontWeight: 700,
              background: (_t) =>
                _t.palette.mode === "dark"
                  ? alpha(_t.palette.warning.main, 0.12)
                  : alpha(_t.palette.warning.main, 0.08),
              color: (_t) => _t.palette.warning.main,
              border: (_t) => `1px solid ${alpha(_t.palette.warning.main, 0.2)}`,
              "& .MuiChip-icon": {
                color: "inherit",
              },
            }}
          />
        </Tooltip>

        <Tooltip key={`followers-${row.id}`} title="Followers" arrow>
          <Chip
            icon={<PersonIcon sx={{ fontSize: 14 }} />}
            label={rowData.followerCount ?? 0}
            size="small"
            sx={{
              height: 24,
              fontSize: "0.7rem",
              fontWeight: 700,
              background: (_t) =>
                _t.palette.mode === "dark"
                  ? alpha(_t.palette.success.main, 0.12)
                  : alpha(_t.palette.success.main, 0.08),
              color: (_t) => _t.palette.success.main,
              border: (_t) => `1px solid ${alpha(_t.palette.success.main, 0.2)}`,
              "& .MuiChip-icon": {
                color: "inherit",
              },
            }}
          />
        </Tooltip>

        {row.parentTask?.title && (
          <Tooltip key={`parent-${row.id}`} title={`Parent: ${row.parentTask.title}`} arrow>
            <Chip
              icon={<SubdirectoryArrowRightIcon sx={{ fontSize: 14 }} />}
              label="Sub"
              size="small"
              sx={{
                height: 24,
                fontSize: "0.7rem",
                fontWeight: 700,
                background: (_t) =>
                  _t.palette.mode === "dark"
                    ? alpha(_t.palette.secondary.main, 0.12)
                    : alpha(_t.palette.secondary.main, 0.08),
                color: (_t) => _t.palette.secondary.main,
                border: (_t) => `1px solid ${alpha(_t.palette.secondary.main, 0.2)}`,
                "& .MuiChip-icon": {
                  color: "inherit",
                },
              }}
            />
          </Tooltip>
        )}
      </Stack>
      );
    },
  },
  {
    id: "actions",
    label: "Actions",
    minWidth: 100,
    align: "center",
    locked: true,
    render: (row, { navigate }) => <ActionsCell row={row} navigate={navigate} />,
  },
];

/* -------------------------------------------------------------------------- */
/*                             Column visibility UI                           */
/* -------------------------------------------------------------------------- */

const STORAGE_KEY = "tasksTable:columns";

const useColumnState = (all: ColumnDef[]) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [visible, setVisible] = React.useState<Record<ColumnId, boolean>>(() => {
    // default: all visible
    const defaults: Record<ColumnId, boolean> = all.reduce((acc, c) => {
      acc[c.id] = true;
      return acc;
    }, {} as Record<ColumnId, boolean>);

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return defaults;
      const parsed = JSON.parse(saved);
      
      // Validate that saved columns match current columns
      const savedColumnIds = Object.keys(parsed);
      const currentColumnIds = all.map(c => c.id);
      
      // If column IDs don't match, reset to defaults
      const hasAllColumns = currentColumnIds.every(id => savedColumnIds.includes(id));
      const hasExtraColumns = savedColumnIds.some(id => !currentColumnIds.includes(id as ColumnId));
      
      if (!hasAllColumns || hasExtraColumns) {
        console.warn("⚠️ Column mismatch detected, resetting to defaults");
        localStorage.setItem(STORAGE_KEY, JSON.stringify(defaults));
        return defaults;
      }
      
      // ensure new columns default to true
      return { ...defaults, ...parsed };
    } catch {
      return defaults;
    }
  });

  const openMenu = Boolean(anchorEl);
  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const toggle = (id: ColumnId, locked?: boolean) => {
    if (locked) return; // don't hide locked columns
    setVisible((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const resetToDefaults = () => {
    const defaults: Record<ColumnId, boolean> = all.reduce((acc, c) => {
      acc[c.id] = true;
      return acc;
    }, {} as Record<ColumnId, boolean>);
    setVisible(defaults);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaults));
  };

  const visibleColumns = all.filter((c) => visible[c.id]);

  return { anchorEl, openMenu, handleOpen, handleClose, toggle, visible, visibleColumns, resetToDefaults };
};

/* -------------------------------------------------------------------------- */
/*                                Component                                   */
/* -------------------------------------------------------------------------- */

const TasksTable: React.FC<TasksTableProps> = ({ rows, isLoading, setOpen, setTaskId, currentUserId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const columns = React.useMemo(() => getColumns(), []);
  
  const { anchorEl, openMenu, handleOpen, handleClose, toggle, visible, visibleColumns, resetToDefaults } =
    useColumnState(columns);

  const helpers: RenderHelpers = {
    navigate,
    currentUserId,
    openStatus: (taskId) => {
      setOpen(true);
      setTaskId(taskId);
    },
  };

  if (isLoading) {
    return (
      <Box 
        sx={{ 
          display: "grid", 
          placeItems: "center", 
          height: "100%", 
          width: "100%",
          minHeight: 400,
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress 
            size={48}
            thickness={4}
            sx={{
              color: theme.palette.primary.main,
            }}
          />
          <Typography 
            variant="body2" 
            color="text.secondary"
            fontWeight={600}
            letterSpacing={0.5}
          >
            Loading tasks...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        flex: 1,
        width: "100%",
        background: theme.palette.mode === "dark"
          ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`
          : `linear-gradient(135deg, ${alpha("#fff", 1)} 0%, ${alpha("#fafafa", 0.95)} 100%)`,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
        boxShadow: theme.palette.mode === "dark"
          ? `0 8px 32px ${alpha("#000", 0.3)}`
          : `0 8px 32px ${alpha("#000", 0.06)}`,
        overflow: "auto",
        maxHeight: "calc(100vh - 280px)",
        position: "relative",
        "&::-webkit-scrollbar": {
          width: 8,
          height: 8,
        },
        "&::-webkit-scrollbar-track": {
          background: theme.palette.mode === "dark"
            ? alpha("#fff", 0.05)
            : alpha("#000", 0.05),
          borderRadius: 4,
        },
        "&::-webkit-scrollbar-thumb": {
          background: theme.palette.mode === "dark"
            ? alpha("#fff", 0.2)
            : alpha("#000", 0.2),
          borderRadius: 4,
          "&:hover": {
            background: theme.palette.mode === "dark"
              ? alpha("#fff", 0.3)
              : alpha("#000", 0.3),
          },
        },
      }}
    >
      <Table 
        stickyHeader 
        size="small" 
        aria-label="professional tasks table"
        sx={{
          tableLayout: "auto", // Auto layout for flexible sizing
          width: "max-content",
          minWidth: "100%",
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              "& th": {
                fontWeight: 800,
                fontSize: "0.8rem",
                letterSpacing: 0.5,
                textTransform: "uppercase",
                background: theme.palette.mode === "dark"
                  ? `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.98)} 100%)`
                  : `linear-gradient(180deg, ${alpha("#fafafa", 1)} 0%, ${alpha("#fff", 0.98)} 100%)`,
                borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                whiteSpace: "nowrap",
                py: 2,
                px: 2,
                color: theme.palette.text.secondary,
              },
            }}
          >
            {visibleColumns.map((col) => (
              <TableCell
                key={col.id}
                align={col.align ?? "left"}
                sx={{ 
                  minWidth: col.minWidth,
                  maxWidth: col.maxWidth || 300,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    maxWidth: "fit-content !important",
                    whiteSpace: "normal",
                    zIndex: 100,
                    boxShadow: `0 4px 16px ${alpha("#000", 0.15)}`,
                  },
                }}
              >
                {col.label}
              </TableCell>
            ))}
            
            {/* Column Settings Button Cell - Sticky right, scrolls horizontally */}
            <TableCell
              align="center"
              sx={{
                position: "sticky",
                right: 0,
                zIndex: 102,
                background: theme.palette.mode === "dark"
                  ? `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.background.default, 0.95)} 15%, ${alpha(theme.palette.background.default, 0.98)} 100%)`
                  : `linear-gradient(90deg, transparent 0%, ${alpha("#fafafa", 0.95)} 15%, ${alpha("#fafafa", 1)} 100%)`,
                backdropFilter: "blur(12px) saturate(180%)",
                borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                py: 2,
                px: 2,
                minWidth: 80,
                boxShadow: `-4px 0 8px ${alpha("#000", theme.palette.mode === "dark" ? 0.15 : 0.05)}`,
              }}
            >
              <Tooltip title="Column Settings" arrow placement="left">
                <IconButton
                  size="small"
                  onClick={handleOpen}
                  sx={{
                    width: 36,
                    height: 36,
                    background: theme.palette.mode === "dark"
                      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`
                      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                    border: `1.5px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                    backdropFilter: "blur(8px)",
                    boxShadow: `0 3px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.28)} 0%, ${alpha(theme.palette.primary.main, 0.18)} 100%)`,
                      transform: "scale(1.15) rotate(90deg)",
                      border: `1.5px solid ${alpha(theme.palette.primary.main, 0.6)}`,
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`,
                    },
                  }}
                >
                  <ViewColumnIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{
                  sx: {
                    borderRadius: 2.5,
                    boxShadow: theme.palette.mode === "dark"
                      ? `0 12px 48px ${alpha("#000", 0.5)}`
                      : `0 12px 48px ${alpha("#000", 0.15)}`,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    minWidth: 240,
                    mt: 1,
                  },
                }}
              >
                <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Visible Columns
                  </Typography>
                </Box>
                <Divider />
                {columns.map((c) => (
                  <MenuItem
                    key={c.id}
                    onClick={() => toggle(c.id, c.locked)}
                    dense
                    disabled={c.locked}
                    sx={{
                      py: 1,
                      px: 2,
                      gap: 1,
                      "&:hover": {
                        background: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    <Checkbox 
                      edge="start" 
                      checked={!!visible[c.id]} 
                      tabIndex={-1}
                      size="small"
                      sx={{
                        "&.Mui-checked": {
                          color: theme.palette.primary.main,
                        },
                      }}
                    />
                    <ListItemText
                      primary={c.label}
                      primaryTypographyProps={{ 
                        variant: "body2",
                        fontWeight: 600,
                      }}
                    />
                    {c.locked && (
                      <Chip
                        label="Required"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          color: theme.palette.warning.main,
                          background: alpha(theme.palette.warning.main, 0.1),
                          border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        }}
                      />
                    )}
                  </MenuItem>
                ))}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ px: 2, pb: 1.5 }}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      resetToDefaults();
                      handleClose();
                    }}
                    sx={{
                      borderRadius: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      py: 0.75,
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      color: theme.palette.primary.main,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        background: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    Reset to Default
                  </Button>
                </Box>
              </Menu>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={visibleColumns.length + 1} align="center" sx={{ py: 8 }}>
                <Fade in timeout={500}>
                  <Stack alignItems="center" spacing={2}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography variant="h2" sx={{ opacity: 0.3 }}>
                        📋
                      </Typography>
                    </Box>
                    <Typography 
                      variant="h6" 
                      color="text.secondary"
                      fontWeight={700}
                    >
                      No tasks to display
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ opacity: 0.7 }}
                    >
                      Try adjusting your filters or create a new task
                    </Typography>
                  </Stack>
                </Fade>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const now = new Date();
              const isOverdue =
                !!row.endDate &&
                new Date(row.endDate).getTime() < now.getTime() &&
                row.status !== TaskStatus.DONE;

              return (
                <TableRow
                  hover
                  key={row.id}
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:nth-of-type(even)": {
                      backgroundColor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha("#fff", 0.02)
                          : alpha("#000", 0.015),
                    },
                    "&:hover": { 
                      backgroundColor: (t) => 
                        t.palette.mode === "dark"
                          ? alpha(t.palette.primary.main, 0.08)
                          : alpha(t.palette.primary.main, 0.04),
                      boxShadow: theme.palette.mode === "dark"
                        ? `0 4px 16px ${alpha("#000", 0.3)}`
                        : `0 4px 16px ${alpha("#000", 0.06)}`,
                    },
                  }}
                >
                  {visibleColumns.map((col, colIndex) => (
                    <TableCell
                      key={`${row.id}-${col.id}`}
                      align={col.align ?? "left"}
                      sx={{ 
                        verticalAlign: "middle",
                        py: 2,
                        px: 2,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        minWidth: col.minWidth,
                        maxWidth: col.maxWidth || 300,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        position: "relative",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        // Add overdue indicator to first column only
                        ...(colIndex === 0 && isOverdue && {
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 4,
                            background: `linear-gradient(180deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
                            boxShadow: `0 0 8px ${alpha(theme.palette.error.main, 0.5)}`,
                          },
                        }),
                        "&:hover": {
                          maxWidth: "fit-content !important",
                          whiteSpace: "normal",
                          zIndex: 50,
                          background: theme.palette.mode === "dark"
                            ? alpha(theme.palette.primary.main, 0.12)
                            : alpha(theme.palette.primary.main, 0.08),
                          boxShadow: `0 6px 24px ${alpha("#000", 0.2)}`,
                          transform: "scale(1.01)",
                        },
                      }}
                    >
                      {col.render(row, helpers)}
                    </TableCell>
                  ))}
                  
                  {/* Empty sticky cell to match header column settings cell */}
                  <TableCell
                    sx={{
                      position: "sticky",
                      right: 0,
                      zIndex: 10,
                      background: theme.palette.mode === "dark"
                        ? `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.background.paper, 0.95)} 15%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
                        : `linear-gradient(90deg, transparent 0%, ${alpha("#fff", 0.95)} 15%, ${alpha("#fff", 1)} 100%)`,
                      backdropFilter: "blur(12px)",
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      py: 2,
                      px: 2,
                      minWidth: 80,
                      boxShadow: `-4px 0 8px ${alpha("#000", theme.palette.mode === "dark" ? 0.15 : 0.05)}`,
                    }}
                  />
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
    </Box>
  );
};

export default TasksTable;
